import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { createPortal } from "react-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineCreditCard,
  HiOutlinePlus,
  HiOutlineMinus,
  HiOutlineExclamationCircle,
  HiOutlineServer,
  HiOutlineCube,
  HiOutlineSparkles,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import { HiOutlineChip, HiOutlineDatabase, HiOutlineX } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { useGetCompaniesQuery } from "reduxFiles/api/companyApi";
import {
  GatewayType,
  GroupType,
  LicenseDTO,
  LicenseScope,
  LicenseTier,
  PlanDto,
} from "types/assessmentTypes";
import { useNavigate } from "react-router-dom";
import { useScope } from "hooks/useScope";
import LicenseList, {
  FilterGroupType,
  FilterState,
  FilterStatus,
} from "./LicenseList";
import {
  useGetLicensesQuery,
  useGetPlansQuery,
  usePurchaseLicenseMutation,
} from "reduxFiles/api/licenseApi";
import Button from "components/button/Button";

type PurchaseFormData = {
  quantity: number;
  groupType: GroupType;
};

type ConfirmModalData = {
  quantity: number;
  totalPrice: number;
  groupType: GroupType;
  tier: LicenseTier;
  planTitle?: string;
  planDescription?: string;
};

const PLAN_CONFIG = {
  MICRO: {
    icon: HiOutlineChip,
    color: "from-indigo-500 to-teal-600",
  },
  SMALL: {
    icon: HiOutlineServer,
    color: "from-blue-500 to-indigo-600",
  },
  MEDIUM: {
    icon: HiOutlineCube,
    color: "from-purple-500 to-pink-600",
  },
  LARGE: {
    icon: HiOutlineDatabase,
    color: "from-orange-500 to-red-600",
  },
};

const PlanItem = ({
  plan,
  isSelected,
  onSelect,
  isNormal,
  tierDescription,
}: {
  plan: PlanDto;
  isSelected: boolean;
  onSelect: () => void;
  isNormal: boolean;
  tierDescription: string;
}) => {
  const { t } = useTranslation();

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer rounded-xl border-2 p-3 transition-all ${
        isSelected
          ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500 ring-offset-2 dark:bg-brand-500/20 dark:ring-offset-navy-900"
          : "border-gray-100 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800/50"
      }`}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between">
          <div
            className={`flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[10px] font-medium ${
              isNormal
                ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                : "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
            }`}
          >
            {isNormal ? (
              <HiOutlineShieldCheck className="h-3 w-3" />
            ) : (
              <HiOutlineSparkles className="h-3 w-3" />
            )}
            {isNormal
              ? t("licenses.tiers.NORMAL")
              : t("licenses.tiers.SPECIAL")}
          </div>
          <span className="text-sm font-bold text-brand-600 dark:text-brand-400">
            {plan.price.toLocaleString()} {t("licenses.priceInit")}
          </span>
        </div>

        <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-gray-700 dark:text-gray-400">
          {tierDescription}
        </p>
      </div>

      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -right-1 -top-1 rounded-full bg-brand-500 p-0.5 text-white"
        >
          <HiOutlineCheckCircle className="h-3.5 w-3.5" />
        </motion.div>
      )}
    </div>
  );
};

const PlanGroupCard = ({
  groupType,
  plans,
  selectedPlanId,
  onPlanSelect,
  isOrganizationScope,
  normalDescription,
  specialDescription,
}: {
  groupType: string;
  plans: PlanDto[];
  selectedPlanId: number | null;
  onPlanSelect: (plan: PlanDto) => void;
  isOrganizationScope: boolean;
  normalDescription: string;
  specialDescription: string;
}) => {
  const { t } = useTranslation();

  const getIcon = () => {
    const config = PLAN_CONFIG[groupType as keyof typeof PLAN_CONFIG];
    return config?.icon || HiOutlineCube;
  };

  const getColor = () => {
    const config = PLAN_CONFIG[groupType as keyof typeof PLAN_CONFIG];
    return config?.color || "from-cyan-500 to-cyan-600";
  };

  const Icon = getIcon();

  const normalPlan = plans.find((p) => p.tier === LicenseTier.NORMAL);
  const specialPlan = plans.find((p) => p.tier === LicenseTier.SPECIAL);

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ duration: 0.2 }}
      className="relative rounded-2xl border-2 border-gray-200 bg-white p-5 shadow-sm transition-all duration-300 hover:border-brand-300 hover:shadow-xl dark:border-gray-700 dark:bg-navy-800"
    >
      <div className="flex items-center gap-3">
        <div
          className={`rounded-xl bg-gradient-to-br p-2.5 text-white ${getColor()}`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-base font-bold text-gray-800 dark:text-white">
          {isOrganizationScope
            ? t("licenses.organizationPlans")
            : t(`group.sizes.${groupType}`)}
        </h3>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {normalPlan && (
          <PlanItem
            plan={normalPlan}
            isSelected={selectedPlanId === normalPlan.id}
            onSelect={() => onPlanSelect(normalPlan)}
            isNormal={true}
            tierDescription={normalDescription}
          />
        )}

        {specialPlan && (
          <PlanItem
            plan={specialPlan}
            isSelected={selectedPlanId === specialPlan.id}
            onSelect={() => onPlanSelect(specialPlan)}
            isNormal={false}
            tierDescription={specialDescription}
          />
        )}
      </div>
    </motion.div>
  );
};

const PurchaseCard = ({
  selectedPlanDetails,
  quantity,
  totalPrice,
  isPlanSelected,
  onIncrease,
  onDecrease,
  onQuantityChange,
  isOrganizationScope,
  t,
}: {
  selectedPlanDetails: PlanDto | null;
  quantity: number;
  totalPrice: number;
  isPlanSelected: boolean;
  onIncrease: () => void;
  onDecrease: () => void;
  onQuantityChange: (value: number) => void;
  isOrganizationScope: boolean;
  t: (key: string) => string;
}) => {
  if (!selectedPlanDetails) {
    return (
      <div className="mt-8 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-8 text-center dark:border-gray-700 dark:bg-gray-800/30">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("licenses.selectPlanFirst")}
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-8 overflow-hidden rounded-2xl border-2 border-brand-200 bg-gradient-to-br from-white to-brand-50/30 shadow-xl dark:border-brand-800/50 dark:from-navy-800 dark:to-brand-900/10"
    >
      {/* Header with selected plan info */}
      <div className="bg-gradient-to-r from-brand-500/10 to-brand-600/5 p-5 dark:from-brand-500/20 dark:to-brand-600/10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div
              className={`rounded-xl p-3 text-white shadow-lg ${
                selectedPlanDetails.tier === LicenseTier.SPECIAL
                  ? "bg-gradient-to-r from-purple-500 to-pink-500"
                  : "bg-gradient-to-r from-blue-500 to-indigo-500"
              }`}
            >
              {selectedPlanDetails.tier === LicenseTier.SPECIAL ? (
                <HiOutlineSparkles className="h-6 w-6" />
              ) : (
                <HiOutlineShieldCheck className="h-6 w-6" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-800 dark:text-white">
                  {isOrganizationScope
                    ? t("licenses.organizationPlans")
                    : t(`group.sizes.${selectedPlanDetails.groupType}`)}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    selectedPlanDetails.tier === LicenseTier.SPECIAL
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                      : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                  }`}
                >
                  {selectedPlanDetails.tier === LicenseTier.SPECIAL
                    ? t("licenses.tiers.SPECIAL")
                    : t("licenses.tiers.NORMAL")}
                </span>
              </div>
              {selectedPlanDetails.meta?.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {selectedPlanDetails.meta.description}
                </p>
              )}
            </div>
          </div>
          <div className="rounded-xl bg-white/80 px-4 py-2 shadow-sm backdrop-blur dark:bg-navy-700/80">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t("licenses.unitPrice")}
            </div>
            <div className="text-lg font-bold text-brand-600 dark:text-brand-400">
              {selectedPlanDetails.price.toLocaleString()}{" "}
              {t("licenses.priceInit")}
            </div>
          </div>
        </div>
      </div>

      {/* Body with quantity and total */}
      <div className="p-6">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          {/* Quantity selector */}
          <div className="flex w-full items-center justify-center gap-4 md:w-auto md:justify-start">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("licenses.quantity")}
            </span>
            <div className="flex items-center gap-1 rounded-2xl bg-gray-100 p-1.5 dark:bg-gray-800">
              <button
                type="button"
                onClick={onDecrease}
                disabled={quantity <= 1 || !isPlanSelected}
                className="flex h-11 w-11 items-center justify-center rounded-xl text-gray-700 transition-all hover:bg-white hover:text-brand-600 hover:shadow-md disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700/50"
              >
                <HiOutlineMinus className="h-5 w-5" />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => {
                  let value = parseInt(e.target.value);
                  if (isNaN(value)) value = 1;
                  if (value < 1) value = 1;
                  if (value > 100) value = 100;
                  onQuantityChange(value);
                }}
                min="1"
                max="100"
                disabled={!isPlanSelected}
                className="dark:bg-transparent w-20 text-center text-2xl font-bold outline-none [appearance:textfield] disabled:cursor-not-allowed [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={onIncrease}
                disabled={quantity >= 100 || !isPlanSelected}
                className="flex h-11 w-11 items-center justify-center rounded-xl text-gray-700 transition-all hover:bg-white hover:text-brand-600 hover:shadow-md disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700/50"
              >
                <HiOutlinePlus className="h-5 w-5" />
              </button>
            </div>
            {quantity >= 100 && (
              <span className="text-xs text-orange-500 dark:text-orange-400">
                {t("licenses.maxLimitReached")}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="hidden h-12 w-px bg-gray-200 dark:bg-gray-700 md:block" />

          {/* Total and purchase button */}
          <div className="flex w-full items-center justify-center gap-4 md:w-auto md:justify-end">
            <div className="text-center">
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t("licenses.totalPayable")}
              </div>
              <div className="text-3xl font-extrabold text-brand-600 dark:text-brand-400">
                {totalPrice.toLocaleString()}
              </div>
              <div className="text-xs text-gray-400">
                {t("licenses.priceInit")}
              </div>
            </div>

            <motion.button
              type="submit"
              whileHover={isPlanSelected ? { scale: 1.03 } : {}}
              whileTap={isPlanSelected ? { scale: 0.97 } : {}}
              disabled={!isPlanSelected}
              className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-brand-500 to-brand-600 px-8 py-4 font-semibold text-white shadow-lg shadow-brand-500/30 transition-all hover:shadow-xl hover:shadow-brand-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:from-brand-600 dark:to-brand-700"
            >
              <HiOutlineCreditCard className="h-5 w-5" />
              {t("licenses.purchase")}
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LicensesPage = () => {
  const { t } = useTranslation();
  const scope = useScope();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);

  const [page, setPage] = useState(1);
  const [licenses, setLicenses] = useState<LicenseDTO[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmModalData | null>(null);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("ALL");
  const [groupTypeFilter, setGroupTypeFilter] =
    useState<FilterGroupType>("ALL");
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanDto | null>(null);

  const isInitializedRef = useRef(false);
  const isUserScope = scope === "user";
  const isOrganizationScope = scope === "organization";

  const { data: companiesData, isLoading: isLoadingCompanies } =
    useGetCompaniesQuery(
      {
        scope: "user",
        page: 1,
        limit: 100,
        filters: {},
      },
      { skip: !isUserScope }
    );

  const { data: plans, isLoading: isLoadingPlans } = useGetPlansQuery({
    filters: {
      organizationId:
        scope === "organization" ? user.organization?.id : undefined,
      isActive: true,
    },
  });

  const companies = companiesData?.items || [];

  const groupPlansByType = (plansData: PlanDto[]) => {
    const grouped = plansData.reduce((acc, plan) => {
      const groupType = plan.groupType || "UNKNOWN";
      if (!acc[groupType]) {
        acc[groupType] = [];
      }
      acc[groupType].push(plan);
      return acc;
    }, {} as Record<string, PlanDto[]>);

    const sortedKeys = Object.keys(grouped).sort();
    const sortedGrouped: Record<string, PlanDto[]> = {};
    sortedKeys.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });
    return sortedGrouped;
  };

  const groupedPlans = plans ? groupPlansByType(plans) : {};
  const planGroupKeys = Object.keys(groupedPlans).sort();

  const getUserCompanySizes = () => {
    if (!isUserScope) return new Set<string>();
    const sizes = new Set<string>();
    companies.forEach((company) => {
      const size = company?.totalStaffCount?.companySize?.toUpperCase();
      if (size) {
        sizes.add(size);
      }
    });
    return sizes;
  };

  const userCompanySizes = getUserCompanySizes();

  const isPlanGroupAllowed = (groupType: string) => {
    if (!isUserScope) return true;
    return userCompanySizes.has(groupType);
  };

  const filteredPlanGroupKeys = planGroupKeys.filter((key) =>
    isPlanGroupAllowed(key)
  );

  const {
    data: licensesData,
    isLoading: isLoadingLicenses,
    refetch,
  } = useGetLicensesQuery({
    scope,
    page,
    limit: 16,
    filters: {
      used:
        statusFilter === "ACTIVE"
          ? false
          : statusFilter === "DEACTIVE"
          ? true
          : undefined,
      groupType: groupTypeFilter === "ALL" ? undefined : groupTypeFilter,
    },
  });

  const [purchaseLicense, { isLoading: isPurchasing }] =
    usePurchaseLicenseMutation();

  const { handleSubmit, watch, setValue } = useForm<PurchaseFormData>({
    defaultValues: {
      quantity: 1,
      groupType: GroupType.MICRO,
    },
  });

  const quantity = watch("quantity");

  const getSelectedPlan = () => {
    if (!selectedPlanId || !plans) return null;
    return plans.find((p) => p.id === selectedPlanId) || null;
  };

  useEffect(() => {
    const plan = getSelectedPlan();
    setSelectedPlan(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId, plans]);

  const selectedPlanDetails = selectedPlan;
  const totalPrice = (selectedPlanDetails?.price || 0) * (quantity || 0);
  const isPlanSelected = selectedPlanId !== null;

  useEffect(() => {
    if (licensesData) {
      setLicenses(licensesData.items || []);
      setTotalPages(licensesData.pageCount || 0);
      setTotalItems(licensesData.total || 0);
    }
  }, [licensesData]);

  useEffect(() => {
    if (!isInitializedRef.current && filteredPlanGroupKeys.length > 0) {
      const firstGroup = filteredPlanGroupKeys[0];
      const firstGroupPlans = groupedPlans[firstGroup];

      if (firstGroupPlans && firstGroupPlans.length > 0) {
        const firstPlan =
          firstGroupPlans.find((p) => p.isActive) || firstGroupPlans[0];
        setSelectedPlanId(firstPlan.id);
        setValue("groupType", firstPlan.groupType || GroupType.MICRO);
        isInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, filteredPlanGroupKeys]);

  useEffect(() => {
    isInitializedRef.current = false;
    setSelectedPlanId(null);
    setSelectedPlan(null);
  }, [scope]);

  const onPurchaseSubmit = (data: PurchaseFormData) => {
    if (!user.id) {
      dispatch(
        addNotification({
          type: "error",
          title: t("licenses.error"),
          message: t("licenses.noUser"),
        })
      );
      return;
    }

    const plan = getSelectedPlan();
    if (!plan) {
      dispatch(
        addNotification({
          type: "error",
          title: t("licenses.error"),
          message: t("licenses.noPlanSelected"),
        })
      );
      return;
    }

    const groupType = plan.groupType || "";
    if (!isPlanGroupAllowed(groupType)) {
      dispatch(
        addNotification({
          type: "error",
          title: t("licenses.error"),
          message: t("licenses.planNotAllowed"),
        })
      );
      return;
    }

    setConfirmData({
      quantity: data.quantity,
      totalPrice: data.quantity * plan.price,
      groupType: data.groupType,
      tier: plan.tier,
      planTitle: plan.title,
      planDescription: plan.meta?.description,
    });
    setShowConfirmModal(true);
  };

  const handleConfirmPurchase = async () => {
    if (!confirmData) return;

    setShowConfirmModal(false);

    try {
      const response = await purchaseLicense({
        quantity: confirmData.quantity,
        gateway: GatewayType.BITPAY,
        userId: user.id,
        email: user.email || "",
        mobile: user.mobile || "",
        groupType: confirmData.groupType,
        scope: LicenseScope.COMPANY,
        tier: confirmData.tier,
        callback: window.location?.href ?? "",
      }).unwrap();

      if (response.redirectUrl) {
        localStorage.setItem(
          "pendingTransaction",
          String(response.transactionId)
        );
        localStorage.setItem("pendingAuthority", response.authority);
        window.location.href = response.redirectUrl;
      } else {
      }
    } catch (err) {
      console.error(err);
    }
  };

  const increaseQuantity = () => {
    if (quantity < 100) {
      setValue("quantity", quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setValue("quantity", quantity - 1);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (filters: FilterState) => {
    setStatusFilter(filters.status);
    setGroupTypeFilter(filters.groupType);
    setPage(1);
  };

  const handlePlanSelect = (plan: PlanDto) => {
    const groupType = plan.groupType || "";
    if (isPlanGroupAllowed(groupType)) {
      setSelectedPlanId(plan.id);
      setValue("groupType", plan.groupType || GroupType.MICRO);
    }
  };

  if (isUserScope && isLoadingCompanies) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader w-8 text-brand-500" />
      </div>
    );
  }

  const ConfirmModal = () => {
    if (!showConfirmModal || !confirmData) return null;

    return createPortal(
      <div className="custom-scrollbar fixed inset-0 z-[9999] overflow-y-auto">
        <div
          className="bg-black/50 fixed inset-0 backdrop-blur-md"
          onClick={() => setShowConfirmModal(false)}
        />
        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 p-5 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-500/20">
                  <HiOutlineExclamationCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                  {t("licenses.confirmPurchase")}
                </h3>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <HiOutlineX className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5">
              <p className="mb-5 text-sm text-gray-800 dark:text-gray-400">
                {t("licenses.confirmMessage")}
              </p>

              <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/50">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-400">
                      {t("licenses.plan")}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {isOrganizationScope
                        ? t("licenses.organizationPlans")
                        : t(`group.sizes.${confirmData.groupType}`)}
                      {confirmData.tier === LicenseTier.SPECIAL
                        ? ` - ${t("licenses.tiers.SPECIAL")}`
                        : ` - ${t("licenses.tiers.NORMAL")}`}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-400">
                      {t("licenses.quantity")}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {confirmData.quantity} {t("licenses.license")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-400">
                      {t("licenses.unitPrice")}
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {(
                        confirmData.totalPrice / confirmData.quantity
                      ).toLocaleString()}{" "}
                      {t("licenses.priceInit")}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {t("licenses.totalPayable")}
                      </span>
                      <span className="text-xl font-bold text-brand-600 dark:text-brand-400">
                        {confirmData.totalPrice.toLocaleString()}{" "}
                        {t("licenses.priceInit")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-xs text-gray-700 dark:text-gray-400">
                {t("licenses.confirmNote")}
              </p>
            </div>

            <div className="flex gap-3 border-t border-gray-100 p-5 dark:border-gray-700">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="dark:bg-transparent flex-1 rounded-xl border border-gray-300 bg-white py-2.5 font-medium transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
              >
                {t("buttons.cancel")}
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleConfirmPurchase}
                disabled={isPurchasing}
                className="flex-1 rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 py-2.5 font-medium text-white transition-all hover:shadow-lg disabled:opacity-50 dark:from-brand-700 dark:to-brand-800"
              >
                {isPurchasing ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="loader w-6 text-brand-500" />
                    {t("licenses.processing")}
                  </div>
                ) : (
                  t("licenses.confirmAndPay")
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>,
      document.body
    );
  };

  const renderScopeInfo = () => {
    if (isUserScope) {
      return (
        <div className="mb-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
          <span className="font-medium">{t("licenses.userScopeInfo")}</span>{" "}
          {t("licenses.userScopeDescription")}
        </div>
      );
    }
  };

  const normalDescription = t("licenses.normalDescription");
  const specialDescription = t("licenses.specialDescription");

  return (
    <div className="min-h-full">
      <div className="p-4 lg:p-6">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                {t("licenses.purchaseLicense")}
              </h2>
            </div>

            {renderScopeInfo()}

            <form onSubmit={handleSubmit(onPurchaseSubmit)}>
              {isLoadingPlans ? (
                <div className="flex justify-center py-12">
                  <div className="loader w-6 text-brand-500" />
                </div>
              ) : filteredPlanGroupKeys.length > 0 ? (
                <div
                  className={`grid gap-4 ${
                    isOrganizationScope
                      ? "mx-auto max-w-2xl grid-cols-1"
                      : "grid-cols-1 md:grid-cols-2"
                  }`}
                >
                  {filteredPlanGroupKeys.map((groupType) => {
                    const groupPlans = groupedPlans[groupType];

                    return (
                      <PlanGroupCard
                        key={groupType}
                        groupType={groupType}
                        plans={groupPlans}
                        selectedPlanId={selectedPlanId}
                        onPlanSelect={handlePlanSelect}
                        isOrganizationScope={isOrganizationScope}
                        normalDescription={normalDescription}
                        specialDescription={specialDescription}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl bg-yellow-50 p-6 text-center dark:bg-yellow-500/10">
                  <p className="text-yellow-700 dark:text-yellow-400">
                    {t("licenses.noPlansAvailable")}
                  </p>
                  <Button
                    variant="info"
                    size="sm"
                    className="mt-3 flex-1"
                    onClick={() => navigate("/contact")}
                  >
                    {t("contact.request")}
                  </Button>
                </div>
              )}

              <div className="mt-10">
                <div className="relative">
                  {/* Decorative divider */}
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                    <span className="bg-white px-4 text-xs uppercase tracking-wider text-gray-400 dark:bg-navy-900 dark:text-gray-500">
                      {t("licenses.summary")}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                </div>

                <PurchaseCard
                  selectedPlanDetails={selectedPlanDetails}
                  quantity={quantity}
                  totalPrice={totalPrice}
                  isPlanSelected={isPlanSelected}
                  onIncrease={increaseQuantity}
                  onDecrease={decreaseQuantity}
                  onQuantityChange={(value) => setValue("quantity", value)}
                  isOrganizationScope={isOrganizationScope}
                  t={t}
                />
              </div>
            </form>
          </motion.div>

          <LicenseList
            licenses={licenses}
            totalItems={totalItems}
            totalPages={totalPages}
            currentPage={page}
            isLoading={isLoadingLicenses}
            onPageChange={handlePageChange}
            onFilterChange={handleFilterChange}
            onRefresh={refetch}
            companies={companies}
          />
        </div>
      </div>

      <ConfirmModal />
    </div>
  );
};

export default LicensesPage;
